import { Badge, Group, Text, Tooltip } from '@mantine/core';
import type { DataLibraryAttendanceRow, AttendanceDisplayStatus } from '../../../types';
import { AttendanceRate, GroupBadge } from '../../common';
import { getAttendanceCoverageDisplay } from '../../../utils/attendanceCoverage';
import { renderCohortHealthBadge } from '../components/CohortHealthBadge';
import { ParticipantEmailPill } from '../components/ParticipantEmailPill';
import { FLAG_SEVERITY, getFlagSeverity } from '../../../config/severityConfig';
import type { ParticipantFlagCategory } from '../../../types';

const PARTICIPANT_STATUS_CONFIG: Record<string, { color: string; label: string; variant: string }> =
  {
    pending: { color: 'yellow', label: 'Pending', variant: 'light' },
    active: { color: 'medleyGreen', label: 'Active', variant: 'light' },
    withdrawn: { color: 'gray', label: 'Withdrawn', variant: 'light' },
  };

const ATTENDANCE_STATUS_CONFIG: Record<
  string,
  { color: string; label: string; variant: string }
> = {
  attended: { color: 'medleyGreen', label: 'Attended', variant: 'light' },
  partial: { color: 'warningAmber', label: 'Partially Attended', variant: 'light' },
  excused_business: { color: 'orange', label: 'Excused — Business', variant: 'light' },
  excused_personal: { color: 'orange', label: 'Excused — Personal', variant: 'light' },
  unexcused: { color: 'dangerRed', label: 'Unexcused Absence', variant: 'light' },
  // MED-1267 (AC21) — the durable withdrawn marker, discoverable in the Data
  // Library attendance table (excluded from attendance-rate math server-side).
  withdrawn: { color: 'gray', label: 'Withdrawn', variant: 'light' },
};

const COHORT_STATUS_CONFIG: Record<string, { color: string; label: string; variant: string }> = {
  live: { color: 'medleyGreen', label: 'Live', variant: 'light' },
  done: { color: 'gray', label: 'Done', variant: 'light' },
  completed: { color: 'gray', label: 'Done', variant: 'light' },
  not_started: { color: 'blue', label: 'Not Started', variant: 'light' },
  draft: { color: 'blue', label: 'Draft', variant: 'light' },
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function AttendancePill({ status }: { status: AttendanceDisplayStatus }) {
  if (!status) {
    return (
      <Text size="xs" c="dimmed" fs="italic">
        Not marked
      </Text>
    );
  }

  const cfg = ATTENDANCE_STATUS_CONFIG[status];
  if (!cfg) return <Text size="sm">—</Text>;

  return (
    <Badge
      size="xs"
      color={cfg.color}
      variant={cfg.variant as 'light' | 'filled' | 'outline'}
      tt="uppercase"
      styles={{ label: { fontSize: 10, letterSpacing: 0.3 } }}
    >
      {cfg.label}
    </Badge>
  );
}

/** MED-1266: coverage-column text — "5 of 6 marked" / "Complete" / "—". */
function coverageColumnLabel(marked: number, population: number): string {
  const d = getAttendanceCoverageDisplay({ rate: 1, marked, population });
  if (d.isUnrecorded) return '—';
  if (d.isComplete) return 'Complete';
  return d.coverageLabel;
}

export function renderAttendanceCell(
  row: DataLibraryAttendanceRow,
  columnId: string,
): React.ReactNode {
  switch (columnId) {
    case 'participant_email':
      return (
        <ParticipantEmailPill
          email={row.participant_email}
          participantId={row.participant_id}
          cohortId={row.cohort_id}
        />
      );
    case 'participant_full_name':
      return <Text size="sm">{row.participant_full_name}</Text>;
    case 'session_number':
      return <Text size="sm">{row.session_number}</Text>;
    case 'session_topic':
      return <Text size="sm">{row.session_topic}</Text>;
    case 'session_date':
      return (
        <Text size="sm" c="dimmed">
          {row.session_date ? formatDate(row.session_date) : '—'}
        </Text>
      );
    case 'cohort_name':
      return <Text size="sm">{row.cohort_name}</Text>;
    case 'cohort_health':
      return renderCohortHealthBadge(row.cohort_health);
    case 'coach_name':
      return <Text size="sm">{row.coach_name || '—'}</Text>;
    case 'group_name':
      return <GroupBadge groupName={row.group_name} groupId={row.group_id} />;
    case 'participant_status': {
      const cfg = PARTICIPANT_STATUS_CONFIG[row.participant_status];
      return (
        <Badge
          size="xs"
          color={cfg?.color || 'gray'}
          variant={(cfg?.variant as 'light' | 'filled') || 'light'}
          tt="uppercase"
          styles={{ label: { fontSize: 10, letterSpacing: 0.3 } }}
        >
          {cfg?.label || row.participant_status}
        </Badge>
      );
    }
    case 'attendance_status':
      return <AttendancePill status={row.attendance_status} />;
    case 'coach_notes': {
      if (!row.coach_notes) return <Text size="sm" c="dimmed">—</Text>;
      const truncated = row.coach_notes.length > 40;
      return (
        <Tooltip label={row.coach_notes} withArrow disabled={!truncated} multiline maw={300}>
          <Text size="sm" truncate style={{ maxWidth: 200 }}>
            {row.coach_notes}
          </Text>
        </Tooltip>
      );
    }
    // MED-1266: rate cells carry marking coverage via the shared component (AC10/AC13).
    case 'overall_attendance_rate':
      return (
        <AttendanceRate
          rate={row.overall_attendance_rate}
          marked={row.overall_attendance_marked}
          population={row.overall_attendance_population}
          variant="compact"
          size="sm"
        />
      );
    case 'session_attendance_rate':
      return (
        <AttendanceRate
          rate={row.session_attendance_rate}
          marked={row.session_attendance_marked}
          population={row.session_attendance_population}
          variant="compact"
          size="sm"
        />
      );
    // MED-1266 (AC12): marking coverage as its own column.
    case 'overall_attendance_coverage':
      return <Text size="sm">{coverageColumnLabel(row.overall_attendance_marked, row.overall_attendance_population)}</Text>;
    case 'session_attendance_coverage':
      return <Text size="sm">{coverageColumnLabel(row.session_attendance_marked, row.session_attendance_population)}</Text>;
    case 'business_unit':
      return <Text size="sm">{row.business_unit || '—'}</Text>;
    case 'department':
      return <Text size="sm">{row.department || '—'}</Text>;
    case 'role':
      return <Text size="sm">{row.role || '—'}</Text>;
    case 'cohort_status': {
      const csCfg = COHORT_STATUS_CONFIG[row.cohort_status?.toLowerCase()] || {
        color: 'gray',
        label: row.cohort_status || '—',
        variant: 'light',
      };
      return (
        <Badge
          size="xs"
          color={csCfg.color}
          variant={csCfg.variant as 'light' | 'filled' | 'outline'}
          tt="uppercase"
          styles={{ label: { fontSize: 10, letterSpacing: 0.3 } }}
        >
          {csCfg.label}
        </Badge>
      );
    }
    case 'location':
      return <Text size="sm">{row.location || '—'}</Text>;
    case 'repeat_medley':
      return <Text size="sm">{row.repeat_medley || '—'}</Text>;
    case 'avg_engagement':
      return (
        <Text size="sm">
          {row.avg_engagement !== null ? Number(row.avg_engagement).toFixed(1) : <>&mdash;</>}
        </Text>
      );
    case 'total_unexcused':
      return <Text size="sm">{row.total_unexcused}</Text>;
    case 'total_missed':
      return <Text size="sm">{row.total_missed}</Text>;
    case 'engagement_rating': {
      if (row.engagement_rating === null || row.engagement_rating === undefined) {
        return <Text size="sm" c="dimmed">&mdash;</Text>;
      }
      const engagementColors: Record<number, { bg: string; text: string; label: string }> = {
        5: { bg: '#28463A', text: '#fff', label: '5 — Highly Engaged' },
        4: { bg: '#4A7C59', text: '#fff', label: '4 — Engaged' },
        3: { bg: '#E9A23B', text: '#fff', label: '3 — Moderate' },
        2: { bg: '#E07B4C', text: '#fff', label: '2 — Low' },
        1: { bg: '#D64545', text: '#fff', label: '1 — Disengaged' },
      };
      const r = Number(row.engagement_rating);
      const config = engagementColors[r] || { bg: '#888', text: '#fff', label: String(r) };
      return (
        <Badge
          size="sm"
          styles={{
            root: { backgroundColor: config.bg },
            label: { color: config.text, fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
          }}
        >
          {config.label}
        </Badge>
      );
    }
    case 'flags': {
      if (!row.flags || row.flags.length === 0) {
        return <Text size="sm" c="dimmed">&mdash;</Text>;
      }
      return (
        <Group gap={4} wrap="nowrap">
          {row.flags.slice(0, 3).map(flag => {
            const severity = getFlagSeverity(flag.flag_category as ParticipantFlagCategory);
            const config = FLAG_SEVERITY[severity];
            return (
              <Tooltip key={flag.id} label={flag.flag_reason} withArrow>
                <Badge
                  size="xs"
                  variant="outline"
                  styles={{
                    root: {
                      borderColor: config.pill.border,
                      color: config.pill.text,
                      backgroundColor: config.pill.background,
                    },
                  }}
                >
                  {flag.flag_category === 'coach_flag'
                    ? 'Coach'
                    : flag.flag_category === 'attendance'
                      ? 'Attend'
                      : flag.flag_category === 'participant_feedback'
                        ? 'Feedback'
                        : 'Engage'}
                </Badge>
              </Tooltip>
            );
          })}
          {row.flags.length > 3 && (
            <Text size="xs" c="dimmed">+{row.flags.length - 3}</Text>
          )}
        </Group>
      );
    }
    default:
      return <Text size="sm">—</Text>;
  }
}
